import {Button, Form, Input, message, Modal, Select, Space, Table, Tag, Tooltip} from 'antd';
import {
    CalendarOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    RetweetOutlined,
    SearchOutlined
} from "@ant-design/icons";
import {Link} from "react-router-dom";
import {WorkbasketTableDto} from "../../../dto/admin/WorkbasketTableDto";
import {
    ADMIN_VERIFICATION_TYPE, BASKET_STATUS_ON_BOARDING_BASKET, BASKET_STATUS_ORIGINALLY_SEEN,
    HH_MM,
    IDENTIFICATION_SOURCE,
    NOT_AVAILABLE,
    TRANSFER_TYPE,
    YYYY_MM_DD,
    YYYY_MM_DD_HH_MM
} from "../../../util/Constants";
import React, {useEffect, useRef, useState} from "react";
import adminService from "../../../service/adminService";
import {
    MSG_ERR_SYS_ERROR,
    MSG_FAILED_TO_ASSIGN_DETAIL_VERIFICATION_ERROR,
    MSG_FAILED_TO_TRANSFER_DETAIL_VERIFICATION_ERROR,
    MSG_TASK_ASSIGN_SUCCESS,
    MSG_TASK_TRANSFER_SUCCESS,
} from "../../../util/Messages";
import ErrorHandler from "../../../util/ErrorHandler";
import modal from 'antd/es/modal';
import dayjs from "dayjs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faHandPointer} from "@fortawesome/free-solid-svg-icons";
import {FilterConfirmProps} from "antd/es/table/interface";
import Highlighter from 'react-highlight-words';
import {AdminRequestDto} from "../../../dto/admin/AdminRequestDto";
import {DropdownListDto} from "../../../dto/DropdownListDto";
import {EyeOutlined} from '@ant-design/icons/lib';
import {InputRef} from 'antd/es/input';
import CommonFunction from "../../../util/CommonFunction";

interface DocumentAndDetailVerificationTable {
    tableData: WorkbasketTableDto[],
    isMyWorkBasket: boolean,
    tableUpdateFunc: (data: boolean) => void,
}

type DataIndex = keyof WorkbasketTableDto;
const DocumentAndDetailsVerificationTable = (props: DocumentAndDetailVerificationTable) => {
    const [tableData, setTableData] = useState<WorkbasketTableDto[]>();
    const [selectedRow, setSelectedRow] = useState<WorkbasketTableDto>();
    const [isTransferModalOpen, setTransferModalOpen] = useState<boolean>(false);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [transferUsername, setTransferUsername] = useState<string>();
    const searchInput = useRef<InputRef>();
    const [activeUserList, setActiveUserList] = useState<DropdownListDto[]>();

    const [form] = Form.useForm();
    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn(dataIndex);
    };
    useEffect(() => {
        setTableData(props.tableData)
    }, [props.tableData])


    const getAdminUsersByType = () => {
        adminService.getAdminUsersByType(TRANSFER_TYPE.DETAIL).then((res) => {
            if (null != res.data.results) {
                let results = res.data.results;
                setActiveUserList(results.activeItemList)
            }
        }).catch((reason) => {
            ErrorHandler.handleError(reason);
        });
    }

    const getColumnSearchProps = (dataIndex: DataIndex, columnName: string): {
        filterDropdown: ({
                             setSelectedKeys,
                             selectedKeys,
                             confirm,
                             clearFilters,
                             close
                         }: {
            setSelectedKeys: any;
            selectedKeys: any;
            confirm: any;
            clearFilters: any;
            close: any
        }) => JSX.Element;
        filterIcon: (filtered: boolean) => JSX.Element;
        onFilter: (value: any, record: any) => boolean;
        render: (text: any) => any;
        onFilterDropdownOpenChange: (visible: any) => void
    } => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
            <div className='table-column-search' onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${columnName}`}
                    value={selectedKeys[0]}
                    onChange={(e: {
                        target: { value: React.Key; };
                    }) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{marginBottom: 12, display: 'block'}}
                />
                <div className='search-btn-row'>
                    <Button className='tbl-search-btn'
                            type="primary"
                            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                            icon={<SearchOutlined/>}
                            size="small"
                    >
                        Search
                    </Button>
                    <Button className='tbl-search-reset'
                            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
                            size="small"
                    >
                        Reset
                    </Button>
                    <Button className='tbl-search-close'
                            size="small"
                            onClick={() => {
                                close();
                            }}
                    >
                        Close
                    </Button>
                </div>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}}/>
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{backgroundColor: '#ffc069', padding: 0}}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const getColor = (identificationSourceType: string) => {
        switch (identificationSourceType) {
            case IDENTIFICATION_SOURCE.NIC: {
                return 'green';
            }
            case IDENTIFICATION_SOURCE.PP: {
                return 'geekblue';
            }
            case IDENTIFICATION_SOURCE.DL: {
                return 'volcano';
            }
            default: {
                return '#8c8c8c';
            }
        }
    }

    const onAssign = (workId: number) => {
        modal.confirm({
            title: 'Confirm',
            icon: <ExclamationCircleOutlined/>,
            content: 'Do you want to assign this task to your work basket?',
            okText: 'Yes',
            cancelText: 'No',
            okButtonProps: {style: {background: '#FF7400'}},
            className: 'confirm-dialog-box',
            onOk() {
                adminService.assignDetailVerificationTaskToWorkbasket(ADMIN_VERIFICATION_TYPE.CUSTOMER_DETAIL_VERIFICATION, workId).then(res => {
                    if (res.data !== null && res.data.results) {
                        message.success(MSG_TASK_ASSIGN_SUCCESS).then();
                        props.tableUpdateFunc(true);
                    } else {
                        message.error(MSG_FAILED_TO_ASSIGN_DETAIL_VERIFICATION_ERROR).then();
                    }
                }, (reason) => {
                    handleError(reason)
                })
            },
        });
    }
    const handleError = (errors: any) => {
        let responseErrors = errors.response;
        if (undefined === responseErrors) {
            message.error(errors.message)
        } else if ("" == responseErrors.data) {
            message.error(MSG_ERR_SYS_ERROR).then();
        } else if (responseErrors.data.errors?.length) {
            if (responseErrors.status == 409) {
                message.warning(responseErrors.data.errors[responseErrors.data.errors.length - 1].errorMessage).then();
                props.tableUpdateFunc(true);
            } else {
                message.error(responseErrors.data.errors[responseErrors.data.errors.length - 1].errorMessage).then();
            }

        } else {
            message.error(MSG_ERR_SYS_ERROR).then();
        }
    }
    const openTransferModal = (record: WorkbasketTableDto) => {
        setTransferModalOpen(true)
        setSelectedRow(record)
        form.setFieldsValue({userName: record.detailVerificationAssignee})
        getAdminUsersByType()
    }
    const submitTransfer = () => {
        setTransferModalOpen(false)
        modal.confirm({
            title: 'Confirm',
            icon: <ExclamationCircleOutlined/>,
            className: 'confirm-dialog-box',
            content: `Do you want to transfer this task to ${transferUsername}?`,
            okText: 'Yes',
            cancelText: 'No',
            okButtonProps: {style: {background: '#FF7400'}},
            onOk() {

                let adminRequestDto = {
                    workId: selectedRow?.workId,
                    adminWorkType: ADMIN_VERIFICATION_TYPE.CUSTOMER_DETAIL_VERIFICATION,
                    username: transferUsername,
                } as AdminRequestDto
                adminService.detailAdminTransferVerificationWork(adminRequestDto).then(res => {
                    if (res.data !== null && res.data.results) {
                        message.success(MSG_TASK_TRANSFER_SUCCESS).then();
                        props.tableUpdateFunc(true);
                        setTransferUsername("")
                    } else {
                        setTransferModalOpen(true)
                        message.error(MSG_FAILED_TO_TRANSFER_DETAIL_VERIFICATION_ERROR).then();
                        setTransferUsername("")
                    }
                }, (reason) => {
                    setTransferModalOpen(true)
                    ErrorHandler.handleError(reason);
                })
            },
            onCancel() {
                setTransferModalOpen(true)
            }
        });
    }

    const handleCancel = () => {
        setTransferModalOpen(false)
        setTransferUsername("")
    }


    const columns = [
        {
            title: 'No',
            dataIndex: 'key',
            render: (text: any, rec: any, index: number) => {
                return (<span>{index + 1}</span>)
            }
        },
        // {
        //     title: 'Priority',
        //     key: 'identificationType',
        //     dataIndex: 'identificationType',
        //     align: 'center',
        //     sorter: (a: any, b: any) => {
        //         a = a.identificationType == null ? '' : a.identificationType
        //         b = b.identificationType == null ? '' : b.identificationType
        //         return a.localeCompare(b);
        //     },
        //     ...getColumnSearchProps('identificationType', 'Identification Type'),
        //     render: (text: any, rec: any) => {
        //         return (
        //             <Tag className={text.toUpperCase() == 'DRIVING LICENSE' ? 'drl-tbl-tag' : 'tbl-tag'} color={'red'} key={text}>
        //                 {'Today'.toUpperCase()}
        //             </Tag>
        //         )
        //     }
        // },
        {
            title: 'Customer Name',
            dataIndex: 'customerName',
            key: 'customerName',
            sorter: (a: any, b: any) => {
                a = a.customerName == null ? '' : a.customerName
                b = b.customerName == null ? '' : b.customerName
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('customerName', 'Customer Name'),
        },
        {
            title: 'RM Code',
            dataIndex: 'rmCode',
            key: 'rmCode',
            sorter: (a: any, b: any) => (a.rmCode - b.rmCode),
            ...getColumnSearchProps('rmCode', 'RM Code'),
            render: (text: any, rec: any) => {
                return (
                    <span>{CommonFunction.validateText(text)}</span>
                )
            },
        },
        {
            title: 'Onboarded By',
            dataIndex: 'basketStatus',
            key: 'basketStatus',
            align: 'center',
            sorter: (a: any, b: any) => {
                a = a.basketStatus == null ? '' : a.basketStatus
                b = b.basketStatus == null ? '' : b.basketStatus
                return a.localeCompare(b);
            },
            filters: [
                {
                    value: 'ORIGINALLY_SEEN',
                    text: "RELATIONSHIP MANAGER",
                },
                {
                    value: 'ON_BOARDING_BASKET',
                    text: "CUSTOMER",
                }
            ],
            onFilter: (value: string, record: any) => record.basketStatus === value,
            render: (text: any, rec: any) => {
                return (getTag(text))
            }
        },
        {
            title: 'Scheduled Date',
            dataIndex: 'verificationScheduleDto',
            key: 'verificationScheduleDto',
            align: 'center',
            sorter: (a: any, b: any) => {
                a = a.verificationScheduleDto.requestedDate == null ? '' : a.verificationScheduleDto.requestedDate
                b = b.verificationScheduleDto.requestedDate == null ? '' : b.verificationScheduleDto.requestedDate
                return a.localeCompare(b);
            },
            render: (text: any, rec: any) => (
                <span>{null != text ? (<span><CalendarOutlined style={{
                    marginRight: 8,
                    opacity: 0.8
                }}/> {dayjs(text.requestedDate).format(YYYY_MM_DD)}</span>) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Scheduled Time Slot',
            dataIndex: 'verificationScheduleDto',
            key: 'verificationScheduleDto',
            align: 'center',
            sorter: (a: any, b: any) => {
                a = a.verificationScheduleDto.requestedStartTime == null ? '' : a.verificationScheduleDto.requestedStartTime
                b = b.verificationScheduleDto.requestedStartTime == null ? '' : b.verificationScheduleDto.requestedStartTime
                return a.localeCompare(b);
            },
            render: (text: any, rec: any) => (
                <span>{null != text ? (<span><ClockCircleOutlined style={{
                    marginRight: 8,
                    opacity: 0.8
                }}/>{dayjs(text.requestedDate + " " + text.requestedStartTime).format(HH_MM)
                    + " - " + dayjs(text.requestedDate + " " + text.requestedEndTime).format(HH_MM)}</span>) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            sorter: (a: any, b: any) => {
                a = a.priority == null ? '' : a.priority
                b = b.priority == null ? '' : b.priority
                return a.localeCompare(b);
            },
            filters: [
                {
                    value: 'LOW',
                    text: "LOW",
                },
                {
                    value: 'HIGH',
                    text: "HIGH",
                },
                {
                    value: 'OVERDUE',
                    text: "OVERDUE",
                },
                {
                    value: 'NORMAL',
                    text: "NORMAL",
                }
            ],
            onFilter: (value: string, record: any) => record.priority === value,
            render: (text: any, rec: any) => {
                return (
                    <Tag
                        className={text == 'LOW' ? 'lowest-level' : text == 'HIGH' ? 'low-level' :
                            text == 'OVERDUE' ? 'high-level' : 'tbl-priority-normal-tag'}
                        color={getColor(text)} key={text}>
                        {text.toUpperCase()}
                    </Tag>
                )
            }
        },
        {
            title: 'Identification Type',
            key: 'identificationType',
            dataIndex: 'identificationType',
            align: 'center',
            sorter: (a: any, b: any) => {
                a = a.identificationType == null ? '' : a.identificationType
                b = b.identificationType == null ? '' : b.identificationType
                return a.localeCompare(b);
            },
            filters: [
                {
                    value: IDENTIFICATION_SOURCE.NIC,
                    text: "NIC",
                },
                {
                    value: IDENTIFICATION_SOURCE.DL,
                    text: "DRIVING LICENSE",
                },
                {
                    value: IDENTIFICATION_SOURCE.PP,
                    text: "PASSPORT",
                }
            ],
            onFilter: (value: string, record: any) => record.identificationType === value,
            render: (text: any, rec: any) => {
                return (
                    <Tag
                        className={getClassName(text)}
                        color={getColor(text)} key={text}>
                        {null != text ? text.toUpperCase() : 'N/A'}
                    </Tag>
                )
            }
        },
        {
            title: 'Identification No',
            dataIndex: 'identificationNumber',
            key: 'identificationNumber',
            sorter: (a: any, b: any) => {
                a = a.identificationNumber == null ? '' : a.identificationNumber
                b = b.identificationNumber == null ? '' : b.identificationNumber
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('identificationNumber', "Identification No"),
            render: (text: any) => {
                return treatNullValuesInTable(text)
            },
        },
        {
            title: 'Residency Status',
            dataIndex: 'residencyStatus',
            key: 'residencyStatus',
            sorter: (a: any, b: any) => {
                a = a.residencyStatus == null ? '' : a.residencyStatus
                b = b.residencyStatus == null ? '' : b.residencyStatus
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('residencyStatus', 'Residency Status'),
        },
        {
            title: 'Created Date Time',
            dataIndex: 'createdDateTime',
            key: 'createdDateTime',
            sorter: (a: any, b: any) => {
                a = a.createdDateTime == null ? '' : a.createdDateTime
                b = b.createdDateTime == null ? '' : b.createdDateTime
                return a.localeCompare(b);
            },
            render: (text: any, rec: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD_HH_MM) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Modified Date Time',
            dataIndex: 'modifiedDatetime',
            key: 'modifiedDatetime',
            sorter: (a: any, b: any) => {
                a = a.modifiedDatetime == null ? '' : a.modifiedDatetime
                b = b.modifiedDatetime == null ? '' : b.modifiedDatetime
                return a.localeCompare(b);
            },
            render: (text: any, rec: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD_HH_MM) : NOT_AVAILABLE}</span>
            )
        },

        {
            title: 'Action',
            key: 'action',
            align: "center",
            fixed: 'right' as const,
            render: (text: any, rec: WorkbasketTableDto) => (
                <div>
                    {props.isMyWorkBasket ?
                        <Space size="middle">
                            <Tooltip placement="top" title='Transfer'>
                                <Button className='btn-action-outline-common btn-action-transfer'
                                        icon={<RetweetOutlined/>}
                                        onClick={() => openTransferModal(rec)}>Transfer</Button>
                            </Tooltip>
                            <Link to={{
                                pathname: '/workflow/admin/documentAndDetailsVerification',
                                state: rec
                            }}>
                                <Tooltip placement="top" title='View'>
                                    <Button className='btn-action-outline-common btn-action-view' icon={<EyeOutlined/>}
                                            onClick={() => localStorage.setItem("workBasketData", JSON.stringify(rec))}>View</Button>
                                </Tooltip>
                            </Link>
                        </Space>
                        :
                        <Button className='btn-action-outline-common btn-action-assign'
                                icon={<FontAwesomeIcon icon={faHandPointer}/>}
                                onClick={() => onAssign(text.workId)}>Assign </Button>

                    }
                </div>
            ),
        },
    ];

    const getTag = (status: string) => {
        switch (status) {
            case BASKET_STATUS_ORIGINALLY_SEEN: {
                return (
                    <Tag className={'drl-tbl-tag'}
                         color='#7cb305' key={status}>
                        RELATIONSHIP MANAGER
                    </Tag>
                )
            }
            case BASKET_STATUS_ON_BOARDING_BASKET: {
                return (
                    <Tag className={'drl-tbl-tag'}
                         color='#1d39c4' key={status}>
                        CUSTOMER
                    </Tag>
                )
            }

        }
    }
    const getClassName = (text: string) => {
        if (null != text) {
            return text.toUpperCase() == "DRIVING LICENSE" ? 'drl-tbl-tag' : 'tbl-tag'
        }
        return 'tbl-tag';
    }

    const treatNullValuesInTable = (value: string) => {
        if (null != value && '' != value) {
            return value;
        }
        return NOT_AVAILABLE;
    }
    return (
        <div>
            <Table scroll={{x: 'max-content'}} columns={columns} dataSource={tableData}/>
            <Modal destroyOnClose={true}
                   maskClosable={false}
                   title="Transfer Task"
                   open={isTransferModalOpen}
                   footer={[]}
                   onCancel={handleCancel}
                   centered
                   className='transfer-task-modal'
            >
                <Form
                    className='form-input attachments-upload-form'
                    layout={"vertical"}
                    form={form}
                    onFinish={submitTransfer}
                >
                    <Form.Item
                        name={"userName"}
                        label="Username"
                        rules={[{
                            required: true,
                            message: 'Please select username to transfer'
                        }]}>
                        <Select placeholder={"Select username"}
                                getPopupContainer={(trigger: { parentNode: any; }) => trigger.parentNode}
                                style={{width: '100%'}}
                                options={activeUserList?.map((user) => ({
                                    value: user.value,
                                    label: user.label,
                                    disabled: user.value === selectedRow?.detailVerificationAssignee,
                                }))}
                                onChange={(e: string) => setTransferUsername(e)}
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input: any, option: {
                                    label: any;
                                }) => (option?.label.toLowerCase() ?? '').includes(input.toLowerCase())}
                        />
                    </Form.Item>
                    <Form.Item className='btn-form-item'>
                        <Space style={{float: 'right'}}>
                            <Button htmlType="submit" className='transfer-btn' style={{
                                cursor: (transferUsername == undefined || transferUsername == "") ? "not-allowed" : "pointer"
                            }}
                                    disabled={transferUsername == undefined || transferUsername == ""}
                                    type="success">Transfer</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>


    )
};


export default DocumentAndDetailsVerificationTable;
